import { useContext, useState, useEffect } from 'react'
import { AppContext } from 'contexts/AppContext'
import { translate } from 'utils/Translate'
import _ from 'lodash'
import { priceConvertor } from 'utils/general'
import { Box, OutlinedInput, Select, MenuItem } from "@mui/material";
import CaptionText from 'components/elements/CaptionText'
import ButtonMedium4 from 'components/elements/ButtonMedium4'
import ContentSmall from 'components/elements/ContentSmall'
import CustomCheckbox from 'components/elements/CustomCheckbox'
import Headline5 from 'components/elements/Headline5'
import ButtonLarge4 from 'components/elements/ButtonLarge4'
import ContentExtraSmall from 'components/elements/ContentExtraSmall'

export function PrintForm({ prints, setPrints, printOptions }) {
  const { brandData, styleGuide } = useContext(AppContext);
  const [enablePrint, setEnablePrint] = useState(false);
  const [printBackup, setPrintBackup] = useState([]);

  const onPrintLineChange = (lines, idx) => {
    setPrints(oldPrints => {
      return oldPrints.map((print, i) => {
        if (i === idx) {
          return { ...print, lines: lines }
        }
        return print
      })
    })
  }

  const onPrintOptionChange = (selectedOptionID, idx) => {
    setPrints(oldPrints => {
      return oldPrints.map((print, i) => {
        if (i === idx) {
          return { ...print, PrintOptionID: selectedOptionID }
        }
        return print
      })
    })
  }

  const onRemovePrint = (idx) => {
    setPrints(oldPrints => oldPrints.filter((_, i) => i !== idx))
  }

  useEffect(() => {
    if (printOptions?.length > 0 && !prints?.length && enablePrint) {
      setPrints([{ PrintOptionID: printOptions[0].PrintOptionID, lines: [''] }])
    }
  }, [printOptions, prints, enablePrint]);

  useEffect(() => {
    if (!enablePrint) {
      setPrintBackup(prints);
      setPrints([]);
    } else if (printBackup?.length) {
      setPrints(printBackup);
      setPrintBackup([]);
    }
  }, [enablePrint]);

  return (
    <Box display={'flex'} flex={1} flexDirection={'column'} flexWrap={'wrap'} alignItems={'stretch'} gap={2} sx={{ border: `1px solid ${styleGuide.color4}`, padding: 2, borderRadius: '10px', maxWidth: 500 }}>
      <Headline5
        translationKey="_PRODUCT_PRINTS_EXPLAINER_"
        fallbackText="Tilføj tryk på produktet"
      />

      <Box display={'flex'} alignItems={'center'}>
        <CustomCheckbox
          checked={enablePrint}
          onChange={() =>
            setEnablePrint(prev => !prev)
          }
        />
        <ContentSmall
          style={{ marginLeft: -12 }}
          translationKey="_PRODUCT_ENABLE_PRINT_LABEL_"
          fallbackText="Jeg ønsker tryk på mit produkt"
        />
      </Box>

      {enablePrint && (
        <Box display={'flex'} gap={1}>
          <ContentExtraSmall>
            {`
              ${translate("_PRODUCT_PRINTS_EXPLAINER_", "Skriv dit tryk med store eller små bogstaver alt efter hvad du ønsker skal trykkes. Kontakt os ved ønske om andre trykmuligheder på")} 
              ${brandData?.contactEmail}
            `}
          </ContentExtraSmall>
        </Box>
      )}

      {enablePrint && prints?.map((print, printIdx) => (
        <Print
          key={printIdx}
          printOptions={printOptions}
          onPrintOptionChange={onPrintOptionChange}
          onRemovePrint={onRemovePrint}
          onPrintLineChange={onPrintLineChange}
          print={print}
          printIdx={printIdx}
        />
      ))}

      {enablePrint &&
        <Box display={'flex'} gap={1} alignItems={'center'} flexWrap={'wrap'} rowGap={1.5}>
          <ButtonLarge4
            showIcon={false}
            onClick={() => setPrints(oldPrints => [...oldPrints, { PrintOptionID: printOptions[0]?.PrintOptionID || 0, lines: [''] }])}
          >
            {translate("_ADD_PRINT_LINE_BUTTON_", "Tilføj ekstra tryk")}
          </ButtonLarge4>
          <ContentExtraSmall
            translationKey="_PRODUCT_PRINTS_EXPLAINER_"
            fallbackText="Du kan tilføje flere tryk med forskellige placeringer."
          />
        </Box>
      }
    </Box>
  )
}

function Print({ printOptions, onPrintOptionChange, onRemovePrint, onPrintLineChange, print, printIdx }) {
  const { brandData, styleGuide } = useContext(AppContext);
  const pointSuffix = translate("_POINTS_SHORT_", "pt");
  const dkkSuffix = translate("_DKK_", "kr");
  const priceSuffix = printOptions?.[0]?.sellingPrice.type === 'POINTS' ? pointSuffix : dkkSuffix;
  const printOption = printOptions.find(option => option.PrintOptionID === print?.PrintOptionID)
  const printVariation = printOption?.variations?.find(v => Number(v.variationSize) === print.lines.length)
  const maxLines = printOption?.variations?.reduce((max, v) => Math.max(max, Number(v.variationSize)), 0) || 0
  const sellingPriceType = printOption?.sellingPriceType

  return (
    <Box display={'flex'} flexDirection={'column'} key={printIdx}>
      <Box display={'flex'} flexDirection={'column'} flex={1} gap={2} sx={{ border: `1px solid ${styleGuide.color4}`, padding: 2, borderRadius: '10px' }}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <CaptionText
            style={{ color: styleGuide.color6 }}
          >
            {translate("_PRODUCT_PRINT_LABEL_", "Tryk")} {printIdx + 1}
          </CaptionText>
          {printIdx > 0 &&
            <ButtonMedium4
              showIcon={false}
              translateionKey="_REMOVE_PRINT_LINE_BUTTON_"
              fallbackText="Fjern"
              onClick={() => {
                onRemovePrint(printIdx)
              }}
            />
          }
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={0.5}>
          <CaptionText
            style={{ color: styleGuide.color6 }}
            translationKey="_PRODUCT_PRINT_PLACEMENT_LABEL_"
            fallbackText="Placering"
          />
          <PrintSelect
            printOptions={printOptions}
            selectedPrintOptionID={print?.PrintOptionID}
            onChange={val => onPrintOptionChange(val, printIdx)}
            sellingPriceType={sellingPriceType}
          />
        </Box>

        <Box display={'flex'} flexDirection={'column'} gap={0.5}>
          <CaptionText
            style={{ color: styleGuide.color6 }}
            translationKey="_PRODUCT_PRINT_LINES_LABEL_"
            fallbackText="Antal linjer"
          />
          <Select
            value={print.lines.length}
            style={{
              borderRadius: 14,
              border: `1px solid ${styleGuide.color4}`,
              fontSize: '14px',
              height: 40,
            }}
            sx={{
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: styleGuide.color16,
              },
            }}
            onChange={(event) => {
              const newSize = Number(event.target.value)
              const newLines = [...print.lines]
              if (newSize > newLines.length) {
                for (let i = newLines.length; i < newSize; i++) {
                  newLines.push('');
                }
              } else {
                newLines.length = newSize;
              }
              onPrintLineChange(newLines, printIdx);
            }}
          >
            {[...Array(maxLines).keys()].map(i => {
              const oneLineVariation = printOption?.variations?.find(v => Number(v.variationSize) === 1)
              const variation = printOption?.variations?.find(v => Number(v.variationSize) === i + 1)
              const additionalLinePrice = (variation?.sellingPrice?.amount - oneLineVariation?.sellingPrice?.amount) || 0
              return (
                <MenuItem key={i} value={i + 1}>
                  {i + 1} {additionalLinePrice > 0 ? `(+${priceConvertor(brandData, additionalLinePrice, priceSuffix)})` : ''}
                </MenuItem>
              )
            })}
          </Select>
        </Box>

        {!!print.PrintOptionID &&
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            {print.lines.map((line, idx) => (
              <Box display={'flex'} flexDirection={'column'} gap={0.5} key={idx}>
                <CaptionText
                  style={{ color: styleGuide.color6 }}
                >
                  {translate("_PRINT_LINE_LABEL_", "Tekstlinje")} {idx + 1}
                </CaptionText>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <OutlinedInput
                    placeholder={translate("_PRINT_LINE_PLACEHOLDER_", "F.eks. dit navn eller et slogan")}
                    value={line}
                    onChange={v => {
                      const newLines = [...print.lines];
                      newLines[idx] = v.target.value;
                      onPrintLineChange(newLines, printIdx);
                    }}
                    sx={{
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: styleGuide.color16,
                      },
                    }}
                    style={{
                      borderRadius: 14,
                      border: `1px solid ${styleGuide.color4}`,
                      fontSize: '14px',
                      width: '100%',
                      height: 40,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        }
        <Box display={'flex'} justifyContent={'end'}>
          <ContentExtraSmall>
            {translate("_PRODUCT_PRINTS_PRICE_", "Pris:")} {printVariation?.sellingPrice ? `${priceConvertor(brandData, printVariation.sellingPrice.amount, priceSuffix)}` : ""}
          </ContentExtraSmall>
        </Box>
      </Box>
    </Box>
  )
}

function PrintSelect({ printOptions, selectedPrintOptionID, onChange }) {
  const { brandData, styleGuide } = useContext(AppContext);
  const pointSuffix = translate("_POINTS_SHORT_", "pt");
  const dkkSuffix = translate("_DKK_", "kr");
  return (
    <Select
      value={selectedPrintOptionID || 0}
      onChange={(event) => onChange(event.target.value)}
      style={{
        borderRadius: 14,
        border: `1px solid ${styleGuide.color4}`,
        fontSize: '14px',
        height: 40,
      }}
      sx={{
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: styleGuide.color16,
        },
      }}
    >
      {printOptions.map((option, idx) => (
        <MenuItem key={idx} value={option.PrintOptionID}>
          {option.title} {option.sellingPrice ? `(${priceConvertor(brandData, option.sellingPrice.amount, option.sellingPrice.type === 'POINTS' ? pointSuffix : dkkSuffix)})` : ''}
        </MenuItem>
      ))}
    </Select>
  )
}